import React, { useState, useEffect, useCallback, useRef } from 'react';
import { printCalendar } from './PrintCalendar';
import CalendarEmailModal from './CalendarEmailModal';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useAuth } from '../../contexts/AuthContext';
import API_URL from '../../config';
import ReservationCard from '../common/ReservationCard';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';

const localizer = momentLocalizer(moment);

// Reservation type colors - change these to update colors throughout the calendar
const RESERVATION_COLORS = {
  'Family': '#3498db',      // Blue
  'Individual': '#9b59b6',  // Purple
  'Renter': '#e67e22',      // Orange
  'default': '#27ae60'      // Green
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];


function Calendar() {
  const [reservations, setReservations] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    type: '',
    description: '',
    notes: '',
    notifyCleaning: false
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Print range state — default to full current year
  const [printYear, setPrintYear] = useState(new Date().getFullYear());
  const [printStartMonth, setPrintStartMonth] = useState(0);
  const [printEndMonth, setPrintEndMonth] = useState(11);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Member selection state for new reservation form (non-admin)
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const memberSearchTimeout = useRef(null);

  // User groups state
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // Admin inline editing state
  const [editingReservation, setEditingReservation] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adminUsers, setAdminUsers] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  // Admin new reservation form state
  const [newAdminReservation, setNewAdminReservation] = useState({
    userId: '',
    checkInDate: '',
    checkOutDate: '',
    status: 'pending',
    type: '',
    year: new Date().getFullYear().toString(),
    description: '',
    notes: '',
    isPublic: false,
    notifyCleaning: false,
    reservationNumber: '',
    totalAmount: ''
  });
  const [calculatingAdminPrice, setCalculatingAdminPrice] = useState(false);
  const [adminCreateLoading, setAdminCreateLoading] = useState(false);

  const { getAuthHeader, isManagerOrAdmin, user: currentUser } = useAuth();
  const isAdminUser = currentUser?.role === 'admin';

  const fetchReservations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/calendar`, {
        headers: getAuthHeader()
      });
      const data = await response.json();
      setReservations(data.reservations || []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Fetch user groups for the group picker (non-admin users)
  useEffect(() => {
    if (isAdminUser) return;
    const fetchUserGroups = async () => {
      try {
        const response = await fetch(`${API_URL}/api/user-groups`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setUserGroups(data.groups || []);
        }
      } catch (err) {
        console.error('Error fetching user groups:', err);
      }
    };
    fetchUserGroups();
  }, [getAuthHeader, isAdminUser]);

  // Fetch users list for admin
  useEffect(() => {
    if (!isAdminUser) return;
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/users`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setAdminUsers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [getAuthHeader, isAdminUser]);

  // Sync selected calendar slot dates into admin new reservation form
  useEffect(() => {
    if (!isAdminUser || !selectedSlot) return;
    setNewAdminReservation(prev => ({
      ...prev,
      checkInDate: moment(selectedSlot.start).format('YYYY-MM-DD'),
      checkOutDate: moment(selectedSlot.end).format('YYYY-MM-DD')
    }));
  }, [selectedSlot, isAdminUser]);

  // Auto-prefill total amount from pricing table for admin new reservation form
  useEffect(() => {
    if (!isAdminUser) return;
    const { checkInDate, checkOutDate, userId } = newAdminReservation;
    if (!userId || !checkInDate || !checkOutDate || checkInDate >= checkOutDate) return;

    let cancelled = false;
    setCalculatingAdminPrice(true);

    const fetchPrice = async () => {
      try {
        const endDateObj = new Date(checkOutDate + 'T00:00:00');
        endDateObj.setDate(endDateObj.getDate() - 1);
        const endDate = endDateObj.toISOString().split('T')[0];

        const res = await fetch(
          `${API_URL}/api/calendar-pricing?startDate=${checkInDate}&endDate=${endDate}`,
          { headers: getAuthHeader() }
        );
        if (!res.ok || cancelled) return;

        const rows = await res.json();
        if (!Array.isArray(rows) || cancelled) return;

        const selectedUser = adminUsers.find(u => String(u.id) === String(userId));
        const isOwner = selectedUser?.owner === true;
        const total = rows.reduce((sum, row) => {
          const rate = isOwner
            ? parseFloat(row.owner_rate || 0)
            : parseFloat(row.renter_rate || 0);
          return sum + rate;
        }, 0);

        if (!cancelled && total > 0) {
          setNewAdminReservation(prev => ({ ...prev, totalAmount: total.toFixed(2) }));
        }
      } catch (err) {
        // silently fail — pricing data may not exist for these dates
      } finally {
        if (!cancelled) setCalculatingAdminPrice(false);
      }
    };

    fetchPrice();
    return () => { cancelled = true; };
  }, [newAdminReservation.checkInDate, newAdminReservation.checkOutDate, newAdminReservation.userId, getAuthHeader, isAdminUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddFromGroup = async (groupId) => {
    if (!groupId) return;
    try {
      const response = await fetch(`${API_URL}/api/user-groups/${groupId}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const groupMembers = data.group.members || [];
        setSelectedMembers(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMembers = groupMembers.filter(m => !existingIds.has(m.id));
          return [...prev, ...newMembers.map(m => ({ id: m.id, name: m.name, email: m.email }))];
        });
        setSelectedGroupIds(prev => prev.includes(parseInt(groupId)) ? prev : [...prev, parseInt(groupId)]);
      }
    } catch (err) {
      console.error('Error fetching group members:', err);
    }
  };

  // Convert reservations to events for react-big-calendar
  const events = reservations.map(res => ({
    id: res.id,
    title: res.description
      ? `${res.reservation_number} - ${res.description}`
      : res.reservation_number,
    start: new Date(res.check_in_date),
    end: new Date(res.check_out_date),
    resource: res,
    type: res.type
  }));

  const handleMemberSearch = (value) => {
    setMemberSearch(value);
    clearTimeout(memberSearchTimeout.current);

    if (value.trim().length < 2) {
      setMemberResults([]);
      return;
    }

    memberSearchTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(value.trim())}`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setMemberResults(data.filter(u => !selectedMembers.some(m => m.id === u.id)));
        }
      } catch (err) {
        console.error('Error searching users:', err);
      }
    }, 300);
  };

  const handleSelectMember = (user) => {
    setSelectedMembers([...selectedMembers, user]);
    setMemberSearch('');
    setMemberResults([]);
  };

  const handleRemoveMember = (userId) => {
    setSelectedMembers(selectedMembers.filter(m => m.id !== userId));
  };

  const handleSelectSlot = ({ start, end: rawEnd }) => {
    // react-big-calendar returns end as the day AFTER the last selected day,
    // so subtract 1 day to get the actual check-out date
    const end = moment(rawEnd).subtract(1, 'day').toDate();

    // Don't allow selecting past dates (admins can select any date)
    if (!isAdminUser && start < new Date().setHours(0, 0, 0, 0)) {
      setMessage('Cannot select past dates');
      return;
    }

    // Need at least 2 days (1 night)
    if (moment(end).isSameOrBefore(moment(start), 'day')) {
      setMessage('Please select at least 2 days (1 night)');
      return;
    }

    // Check overlap only for non-admin users
    if (!isAdminUser) {
      const isOverlapping = events.some(event => {
        const eventStart = new Date(event.start).setHours(0, 0, 0, 0);
        const eventEnd = new Date(event.end).setHours(0, 0, 0, 0);
        const selStart = new Date(start).setHours(0, 0, 0, 0);
        const selEnd = new Date(end).setHours(0, 0, 0, 0);
        return selStart < eventEnd && selEnd > eventStart;
      });

      if (isOverlapping) {
        setMessage('Selected dates overlap with existing reservation');
        return;
      }
    }

    setSelectedSlot({ start, end });
    setSelectedEvent(null);
    setEditingReservation(null);
    setEditingId(null);
    setMessage('');
  };

  const handleSelectEvent = async (event) => {
    if (isAdminUser) {
      // Admin: fetch full reservation and show inline ReservationCard
      setSelectedEvent(null);
      setSelectedSlot(null);
      setEditingId(null);
      setEditLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/reservations/${event.id}`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setEditingReservation(data.request || data);
        }
      } catch (err) {
        console.error('Error fetching reservation:', err);
      } finally {
        setEditLoading(false);
      }
    } else {
      // Non-admin: show simple modal
      setSelectedEvent(event);
      setSelectedSlot(null);
      setEditingReservation(null);
    }
  };

  const refetchEditingReservation = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const reservation = data.request || data;
        const user_name = adminUsers.find(u => u.email === reservation.email)?.name || null;
        setEditingReservation({ ...reservation, user_name });
      }
    } catch (err) {
      console.error('Error refetching reservation:', err);
    }
  };

  const handleAddPayment = async (reservationId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/reservation/${reservationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        await refetchEditingReservation(reservationId);
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to add payment'}`);
        return { success: false };
      }
    } catch (error) {
      alert('Failed to add payment');
      return { success: false };
    }
  };

  const handleUpdatePayment = async (paymentId, paymentData) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(paymentData)
      });
      const data = await response.json();
      if (data.success) {
        await refetchEditingReservation(editingReservation.id);
        return { success: true, payment: data.payment };
      } else {
        alert(`Error: ${data.error || 'Failed to update payment'}`);
        return { success: false };
      }
    } catch (error) {
      alert('Failed to update payment');
      return { success: false };
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        await refetchEditingReservation(editingReservation.id);
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to delete payment'}`);
        return { success: false };
      }
    } catch (error) {
      alert('Failed to delete payment');
      return { success: false };
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/verify`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (data.success) {
        await refetchEditingReservation(editingReservation.id);
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to verify payment'}`);
        return { success: false };
      }
    } catch (error) {
      alert('Failed to verify payment');
      return { success: false };
    }
  };

  const handleRejectPayment = async (paymentId, reason) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        await refetchEditingReservation(editingReservation.id);
        return { success: true };
      } else {
        alert(`Error: ${data.error || 'Failed to reject payment'}`);
        return { success: false };
      }
    } catch (error) {
      alert('Failed to reject payment');
      return { success: false };
    }
  };

  const handleCreateAdminReservation = async (e) => {
    e.preventDefault();

    if (!newAdminReservation.userId || !newAdminReservation.checkInDate || !newAdminReservation.checkOutDate) {
      alert('Please fill in user, check-in date, and check-out date.');
      return;
    }

    setAdminCreateLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/admin-create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(newAdminReservation)
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Reservation created successfully! (${data.request.status})`);
        setNewAdminReservation({
          userId: '',
          checkInDate: '',
          checkOutDate: '',
          status: 'pending',
          type: '',
          year: new Date().getFullYear().toString(),
          description: '',
          notes: '',
          isPublic: false,
          notifyCleaning: false,
          reservationNumber: '',
          totalAmount: ''
        });
        setSelectedSlot(null);
        fetchReservations();
      } else {
        alert(`Error: ${data.error || 'Failed to create reservation'}`);
      }
    } catch (error) {
      alert('Failed to create reservation');
    } finally {
      setAdminCreateLoading(false);
    }
  };

  const handleUpdateReservation = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.success) {
        const user_name = adminUsers.find(u => u.email === data.request.email)?.name || null;
        setEditingReservation({ ...editingReservation, ...data.request, user_name });
        setEditingId(null);
        fetchReservations();
      }
    } catch (error) {
      console.error('Error updating reservation:', error);
    }
  };

  const handleCancelReservation = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }

    const reason = prompt('Enter cancellation reason (optional):');
    if (reason === null) return;

    try {
      const response = await fetch(`${API_URL}/api/reservations/cancel/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ reason })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Reservation cancelled successfully');
        setSelectedEvent(null);
        fetchReservations();
      } else {
        setMessage(data.error || 'Failed to cancel reservation');
      }
    } catch (error) {
      setMessage('Error cancelling reservation');
    }
  };

  const handleSubmit = async () => {
    if (!selectedSlot) return;

    const nights = moment(selectedSlot.end).diff(moment(selectedSlot.start), 'days');
    if (nights < 1) {
      setMessage('Check-out must be at least 1 day after check-in');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/reservations/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          checkInDate: moment(selectedSlot.start).format('YYYY-MM-DD'),
          checkOutDate: moment(selectedSlot.end).format('YYYY-MM-DD'),
          notifyCleaning: formData.notifyCleaning,
          description: formData.description,
          notes: formData.notes,
          year: formData.year,
          type: formData.type
        })
      });

      const data = await response.json();

      if (data.success) {
        const reservationId = data.request.id;

        if (selectedGroupIds.length > 0) {
          await Promise.all(selectedGroupIds.map(groupId =>
            fetch(`${API_URL}/api/reservations/${reservationId}/members/from-group`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
              body: JSON.stringify({ groupId })
            })
          ));
        }

        if (selectedMembers.length > 0) {
          await Promise.all(selectedMembers.map(member =>
            fetch(`${API_URL}/api/reservations/${reservationId}/members`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
              body: JSON.stringify({ userId: member.id })
            }).catch(() => {})
          ));
        }

        setMessage('Reservation request submitted!');
        setSelectedSlot(null);
        setSelectedMembers([]);
        setSelectedGroupIds([]);
        setMemberSearch('');
        setFormData({
          year: new Date().getFullYear(),
          type: '',
          description: '',
          notes: '',
          notifyCleaning: false
        });
        fetchReservations();
      } else {
        setMessage(data.error || 'Failed to submit reservation.');
      }
    } catch (error) {
      setMessage('Error submitting reservation.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printEvents = reservations.map(res => ({
      id: res.id,
      title: res.description
        ? `${res.reservation_number} - ${res.description}`
        : res.reservation_number,
      type: res.type,
      startStr: (res.check_in_date || '').split('T')[0],
      endStr: (res.check_out_date || '').split('T')[0],
    }));
    printCalendar(printYear, printStartMonth, printEndMonth, printEvents, new Date());
  };

  const dayPropGetter = (date) => {
    if (!selectedSlot) return {};
    const day = new Date(date).setHours(0, 0, 0, 0);
    const start = new Date(selectedSlot.start).setHours(0, 0, 0, 0);
    const end = new Date(selectedSlot.end).setHours(0, 0, 0, 0);
    if (day >= start && day <= end) {
      return { className: 'rbc-day-selected' };
    }
    return {};
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = RESERVATION_COLORS[event.type] || RESERVATION_COLORS['default'];
    const isPast = event.end < new Date();

    return {
      style: {
        backgroundColor,
        opacity: isPast ? 0.5 : 1,
        border: 'none',
        borderRadius: '4px',
        color: 'white',
        fontWeight: '600',
        fontSize: '0.85rem'
      }
    };
  };

  return (
    <div className="calendar-container">
      <div className="panel calendar-wrapper">
        <div className="cal-header-row">
          <div className="cal-jump-to">
            <label htmlFor="cal-jump-date">Jump to date:</label>
            <input
              id="cal-jump-date"
              type="date"
              value={moment(calendarDate).format('YYYY-MM-DD')}
              onChange={(e) => {
                const d = new Date(e.target.value + 'T00:00:00');
                if (!isNaN(d)) setCalendarDate(d);
              }}
            />
          </div>
          <div className="cal-print-controls">
            <select
              className="cal-print-select"
              value={printYear}
              onChange={(e) => setPrintYear(parseInt(e.target.value))}
            >
              {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1, new Date().getFullYear() + 2].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              className="cal-print-select"
              value={printStartMonth}
              onChange={(e) => setPrintStartMonth(parseInt(e.target.value))}
            >
              {MONTH_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <span className="cal-print-dash">–</span>
            <select
              className="cal-print-select"
              value={printEndMonth}
              onChange={(e) => setPrintEndMonth(parseInt(e.target.value))}
            >
              {MONTH_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <button
              className="btn btn-neutral"
              onClick={handlePrint}
            >
              Print / Save PDF
            </button>
            {isAdminUser && (
              <button
                className="btn btn-primary"
                onClick={() => setShowEmailModal(true)}
              >
                Email Calendar
              </button>
            )}
          </div>
          <button
            className="btn btn-primary new-reservation-btn"
            onClick={async () => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);
              setSelectedSlot({ start: today, end: tomorrow });
              setSelectedEvent(null);
              setEditingReservation(null);
              setEditingId(null);
              setSelectedMembers([]);
              setSelectedGroupIds([]);
              setMemberSearch('');
              setMessage('');

              if (isAdminUser) {
                try {
                  const res = await fetch(`${API_URL}/api/reservations/next-number`, {
                    headers: getAuthHeader()
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setNewAdminReservation(prev => ({
                      ...prev,
                      reservationNumber: data.reservationNumber,
                      checkInDate: moment(today).format('YYYY-MM-DD'),
                      checkOutDate: moment(tomorrow).format('YYYY-MM-DD')
                    }));
                  }
                } catch (_) {}
              }
            }}
          >
            + New Reservation
          </button>
        </div>
      {/* Admin new reservation form */}
      {selectedSlot && isAdminUser && (
        <div className="calendar-selection">
          <h3>New Reservation</h3>
          <form className="create-reservation-form" onSubmit={handleCreateAdminReservation}>
            <div className="form-row">
              <div className="form-group">
                <label>User *</label>
                <select
                  value={newAdminReservation.userId}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, userId: e.target.value })}
                  required
                >
                  <option value="">Select a user...</option>
                  {adminUsers.filter(u => u.is_active).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={newAdminReservation.status}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Check-in Date *</label>
                <input
                  type="date"
                  value={newAdminReservation.checkInDate}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, checkInDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Check-out Date *</label>
                <input
                  type="date"
                  value={newAdminReservation.checkOutDate}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, checkOutDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Reservation Number</label>
                <input
                  type="text"
                  value={newAdminReservation.reservationNumber}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, reservationNumber: e.target.value })}
                  placeholder="e.g. RES-2026-0001"
                />
              </div>
              <div className="form-group">
                <label>Total Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newAdminReservation.totalAmount}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, totalAmount: e.target.value })}
                  placeholder={calculatingAdminPrice ? 'Calculating...' : 'e.g. 750.00'}
                  disabled={calculatingAdminPrice}
                />
                {calculatingAdminPrice && (
                  <small style={{ color: '#888' }}>Fetching rate from pricing calendar...</small>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <input
                  type="text"
                  value={newAdminReservation.type}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, type: e.target.value })}
                  placeholder="e.g. Individual, Family, Renter"
                />
              </div>
              <div className="form-group">
                <label>Year</label>
                <input
                  type="number"
                  value={newAdminReservation.year}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, year: e.target.value })}
                  placeholder="e.g. 2026"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newAdminReservation.description}
                onChange={(e) => setNewAdminReservation({ ...newAdminReservation, description: e.target.value })}
                placeholder="Reservation description..."
                rows="2"
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={newAdminReservation.notes}
                onChange={(e) => setNewAdminReservation({ ...newAdminReservation, notes: e.target.value })}
                placeholder="Internal notes..."
                rows="2"
              />
            </div>
            <div className="form-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newAdminReservation.isPublic}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, isPublic: e.target.checked })}
                />
                Public
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newAdminReservation.notifyCleaning}
                  onChange={(e) => setNewAdminReservation({ ...newAdminReservation, notifyCleaning: e.target.checked })}
                />
                Notify Cleaning
              </label>
            </div>
            <div className="form-row">
              <button type="submit" className="btn btn-success" disabled={adminCreateLoading}>
                {adminCreateLoading ? 'Creating...' : 'Create Reservation'}
              </button>
              <button
                type="button"
                className="btn btn-neutral"
                onClick={() => setSelectedSlot(null)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Non-admin new reservation form */}
      {selectedSlot && !isAdminUser && (
        <div className="calendar-selection">
          <h3>New Reservation</h3>
          <div className="reservation-dates-row">
            <div className="date-field">
              <label htmlFor="check-in-date">Check-in</label>
              <input
                id="check-in-date"
                type="date"
                value={moment(selectedSlot.start).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const d = new Date(e.target.value + 'T00:00:00');
                  if (!isNaN(d)) {
                    setSelectedSlot({ ...selectedSlot, start: d });
                  }
                }}
              />
            </div>
            <div className="date-arrow">→</div>
            <div className="date-field">
              <label htmlFor="check-out-date">Check-out</label>
              <input
                id="check-out-date"
                type="date"
                value={moment(selectedSlot.end).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const d = new Date(e.target.value + 'T00:00:00');
                  if (!isNaN(d)) {
                    setSelectedSlot({ ...selectedSlot, end: d });
                  }
                }}
              />
            </div>
            <div className="nights-display">
              <strong>{moment(selectedSlot.end).diff(moment(selectedSlot.start), 'days')}</strong> night{moment(selectedSlot.end).diff(moment(selectedSlot.start), 'days') !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="reservation-fields">
            <div className="reservation-row">
              <input
                type="number"
                placeholder="Year"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                min={new Date().getFullYear()}
              />
            </div>
            <textarea
              placeholder="Description (e.g., family reunion, birthday trip)"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={2}
            />
            <textarea
              placeholder="Notes (e.g., arriving late, need extra towels)"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={2}
            />
            <label className="cleaning-checkbox">
              <input
                type="checkbox"
                checked={formData.notifyCleaning}
                onChange={(e) => setFormData({...formData, notifyCleaning: e.target.checked})}
              />
              Notify cleaning service
            </label>

            <div className="member-picker">
              <label className="member-picker-label">Add Members (optional)</label>
              {userGroups.length > 0 && (
                <div className="group-picker">
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddFromGroup(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Add from group...</option>
                    {userGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.member_count})</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedMembers.length > 0 && (
                <div className="selected-members">
                  {selectedMembers.map(m => (
                    <span key={m.id} className="selected-member-chip">
                      {m.name}
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={() => handleRemoveMember(m.id)}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="member-search-wrap">
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => handleMemberSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="member-search-input"
                />
                {memberResults.length > 0 && (
                  <div className="member-results-dropdown">
                    {memberResults.map(u => (
                      <div
                        key={u.id}
                        className="member-result-item"
                        onClick={() => handleSelectMember(u)}
                      >
                        <span className="result-name">{u.name}</span>
                        <span className="result-email">{u.email}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              className="btn btn-success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Reservation'}
            </button>
          </div>
        </div>
      )}

      {editLoading && (
        <div className="calendar-selection">
          <p>Loading reservation...</p>
        </div>
      )}

      {editingReservation && !editLoading && (
        <div className="calendar-selection">
          <div className="calendar-edit-header">
            <h3>Reservation Details</h3>
            <button
              className="btn btn-neutral"
              onClick={() => { setEditingReservation(null); setEditingId(null); }}
            >
              Close
            </button>
          </div>
          <ReservationCard
            reservation={{
              ...editingReservation,
              user_name: editingReservation.user_name || adminUsers.find(u => u.email === editingReservation.email)?.name
            }}
            isEditing={editingId === editingReservation.id}
            editForm={editForm}
            users={adminUsers}
            canEditPayments={true}
            canDeletePayments={true}
            getAuthHeader={getAuthHeader}
            onAddPayment={handleAddPayment}
            onUpdatePayment={handleUpdatePayment}
            onDeletePayment={handleDeletePayment}
            onVerifyPayment={handleVerifyPayment}
            onRejectPayment={handleRejectPayment}
            onEdit={() => {
              setEditingId(editingReservation.id);
              setEditForm({
                reservation_number: editingReservation.reservation_number || '',
                check_in_date: editingReservation.check_in_date?.split('T')[0] || '',
                check_out_date: editingReservation.check_out_date?.split('T')[0] || '',
                year: editingReservation.year || '',
                type: editingReservation.type || '',
                description: editingReservation.description || '',
                notes: editingReservation.notes || '',
                status: editingReservation.status,
                total_amount: editingReservation.total_amount != null ? String(editingReservation.total_amount) : '',
                email: editingReservation.email || ''
              });
            }}
            onSave={() => {
              handleUpdateReservation(editingReservation.id, editForm);
            }}
            onCancel={() => setEditingId(null)}
            onChange={(updates) => setEditForm({ ...editForm, ...updates })}
          />
        </div>
      )}

        <BigCalendar
          localizer={localizer}
          date={calendarDate}
          onNavigate={(date) => setCalendarDate(date)}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          dayPropGetter={dayPropGetter}
          views={['month']}
          defaultView="month"
          popup
        />
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: RESERVATION_COLORS['Family'] }}></span>
          <span>Family</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: RESERVATION_COLORS['Individual'] }}></span>
          <span>Individual</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: RESERVATION_COLORS['Renter'] }}></span>
          <span>Renter</span>
        </div>
      </div>

      <div className="calendar-instructions">
        <p>Click and drag on the calendar to select your check-in and check-out dates</p>
      </div>

      {selectedEvent && !isAdminUser && (
        <div className="reservation-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="reservation-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedEvent(null)}>&times;</button>
            <h3>Reservation Details</h3>
            <div className="modal-content">
              <p><strong>Reservation #:</strong> {selectedEvent.resource.reservation_number}</p>
              <p><strong>Check-in:</strong> {moment(selectedEvent.start).format('MMM DD, YYYY')}</p>
              <p><strong>Check-out:</strong> {moment(selectedEvent.end).format('MMM DD, YYYY')}</p>
              {selectedEvent.resource.description && (
                <p><strong>Description:</strong> {selectedEvent.resource.description}</p>
              )}

              {isManagerOrAdmin() && (
                <>
                  <p><strong>Email:</strong> {selectedEvent.resource.email}</p>
                  <button
                    className="btn btn-neutral"
                    onClick={() => handleCancelReservation(selectedEvent.id)}
                  >
                    Cancel Reservation
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`calendar-message ${message.includes('submitted') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {showEmailModal && (
        <CalendarEmailModal
          users={adminUsers}
          printYear={printYear}
          printStartMonth={printStartMonth}
          printEndMonth={printEndMonth}
          getAuthHeader={getAuthHeader}
          onClose={() => setShowEmailModal(false)}
        />
      )}

    </div>
  );
}

export default Calendar;
